import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import { type AccentHue, accentHues } from "@repo/fika-design-system/color"
import type { IconName } from "@repo/fika-design-system/icon"
import { createMemo } from "solid-js"
import { useEditorIsActive } from "solid-tiptap"
import { Mini } from "@/designSystem/components/mini/mini"
import { Tooltip } from "@/designSystem/components/tooltip"
import { useI18nContext } from "@/i18n/i18n-solid"

const NO_HIGHLIGHT = "no-highlight"
const COLOR_ICONS = {
  sand: "wind",
  amber: "citrus",
  indigo: "droplet",
  emerald: "leaf",
  orange: "carrot",
  rose: "cherry",
} as const

interface HighlightSelectProps {
  editor: Editor
}

export function HighlightSelect(props: HighlightSelectProps) {
  const { LL } = useI18nContext()

  const highlightValue = createMemo(() => {
    const match = useEditorIsActive(
      () => props.editor,
      () => "highlight",
    )
    if (!match()) return undefined
    const color = props.editor.getAttributes("highlight").color as AccentHue
    return color
  })

  const selectValue = createMemo(() => highlightValue() ?? NO_HIGHLIGHT)

  function getColorLabel(hue: AccentHue) {
    const colorMap = {
      sand: LL().editor.grey(),
      amber: LL().editor.yellow(),
      indigo: LL().editor.blue(),
      emerald: LL().editor.green(),
      orange: LL().editor.orange(),
      rose: LL().editor.red(),
      neutral: LL().editor.grey(),
    }
    return colorMap[hue]
  }

  function highlightOnChange(value: string | null) {
    if (!value || value === NO_HIGHLIGHT) {
      props.editor.chain().focus().unsetMark("highlight").run()
      return
    }

    props.editor
      .chain()
      .focus()
      .setMark("highlight", { color: value as AccentHue })
      .run()
  }

  return (
    <Tooltip description={LL().editor.highlightColor()} hue={highlightValue()}>
      <Mini.Select
        items={[
          {
            value: NO_HIGHLIGHT,
            label: LL().editor.noHighlight(),
            iconName: "x" as IconName,
            hue: "sand",
          },
          ...accentHues
            .filter(value => value !== "neutral")
            .map(value => ({
              value,
              label: getColorLabel(value),
              iconName: COLOR_ICONS[value],
              hue: value,
            })),
        ]}
        value={selectValue()}
        onChange={highlightOnChange}
        fixedIcon="highlighter"
        hideValue
        portal={false}
        hideChevron
      />
    </Tooltip>
  )
}
